import { FunctionComponent, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { actAsApi } from "core/api";
import { formatActAsSubject } from "core/api/act-as/act-as.helpers";
import { useActAs } from "contexts/ActAsContext";
import { ActAsCandidate } from "core/types/act-as.types";
import { Text } from "components/atoms/text";

interface Props {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
}

const ActAsPicker: FunctionComponent<Props> = ({ isVisible, setIsVisible }) => {
  const [t] = useTranslation();
  const { start, isStarting } = useActAs();
  const [candidates, setCandidates] = useState<ActAsCandidate[]>([]);
  const [selected, setSelected] = useState<ActAsCandidate | null>(null);
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!isVisible) return;
    let cancelled = false;
    setError(undefined);
    setIsLoading(true);
    actAsApi
      .listCandidates()
      .then((list) => {
        if (cancelled) return;
        // Solo account attivi: aprire una delega su un utente disabilitato produce
        // pratiche intestate a un account che non potra' mai aprirle.
        setCandidates(
          list
            .filter((candidate) => candidate.enabled !== false)
            .sort((a, b) =>
              formatActAsSubject(a).localeCompare(formatActAsSubject(b))
            )
        );
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setError(t("act-as.load-error"));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isVisible, t]);

  const handleClose = () => {
    setSelected(null);
    setReason("");
    setError(undefined);
    setIsVisible(false);
  };

  // Il motivo e' OBBLIGATORIO, e il gate vero e' server-side (models/act_as_model.py:
  // open_session rifiuta senza motivo). Qui si evita solo di far fare il giro a una
  // richiesta che verra' rifiutata. E' la riga che, fra sei mesi, risponde alla domanda
  // "perche' questo operatore ha visto i dati di questa persona?".
  const isReasonValid = reason.trim().length >= 3;

  const handleConfirm = async () => {
    if (!selected || !isReasonValid) return;
    setError(undefined);
    try {
      // In caso di successo la pagina si ricarica (ActAsContext): il dialog non va chiuso.
      await start(selected.id, reason.trim());
    } catch (err: any) {
      console.error(err);
      const data = err?.response?.data;
      if (data?.code === "act_as_disabled") {
        // ACT_AS_ENABLED spento su questo ambiente: e' un rifiuto esplicito, non un
        // errore da riprovare.
        setError(t("act-as.disabled-error"));
      } else {
        setError(data?.error || t("act-as.start-error"));
      }
    }
  };

  return (
    <Dialog open={isVisible} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("act-as.dialog-title")}</DialogTitle>
      <DialogContent>
        <Text mb={4}>{t("act-as.dialog-description")}</Text>
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}
        <Autocomplete
          id="autocomplete-act-as"
          value={selected}
          onChange={(event, newValue) => setSelected(newValue)}
          options={candidates}
          loading={isLoading}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) =>
            `${formatActAsSubject(option)} — ${option.email}`
          }
          noOptionsText={t("act-as.no-customers")}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("act-as.customer-label")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Box mt={4}>
          <TextField
            fullWidth
            required
            label={t("act-as.reason-label")}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            inputProps={{ maxLength: 255 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={isStarting}>
          {t("act-as.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selected || !isReasonValid || isStarting}
          startIcon={isStarting ? <CircularProgress size={16} /> : undefined}
        >
          {t("act-as.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActAsPicker;
